import { useEffect, useState, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authService } from '../../services/authService'
import { LoadingScreen } from '../ui/LoadingScreen'

interface ProgressGuardProps {
  children: ReactNode
}

// Rutas del menú de cuenta (sidebar de "Mis cotizaciones" y hermanas) en las
// que el usuario puede navegar libremente aunque tenga un progressUrl
// activo — comportamiento híbrido, válido para cualquier solicitud (1ra o
// posterior): se le sigue ofreciendo "Continuar" para retomar su
// progressUrl, pero no queda atrapado ahí. Los pasos de onboarding en sí
// (fuera de esta lista) siguen protegidos como hoy.
const ACCOUNT_MENU_ROUTES = [
  '/onboarding/pending-proposals',
  '/mis-arrendamientos',
  '/mis-documentos',
  '/mi-perfil',
  '/configuracion',
]

// Coincidencia exacta o de una ruta hija directa (p. ej.
// "/mis-arrendamientos/abc123" cuenta como parte de "/mis-arrendamientos")
// — así no hace falta listar cada ruta de detalle por separado.
const isAccountMenuRoute = (pathname: string) =>
  ACCOUNT_MENU_ROUTES.some(
    route => pathname === route || pathname.startsWith(route + '/')
  )

export const ProgressGuard = ({ children }: ProgressGuardProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isCheckingRestriction, setIsCheckingRestriction] = useState(false)

  useEffect(() => {
    const checkProgressCompliance = async () => {
      console.log('🔍 ProgressGuard: Checking progress compliance...')

      // Verificar si el usuario está autenticado
      if (!authService.isAuthenticated()) {
        console.log('🔓 User not authenticated, allowing navigation')
        return
      }

      setIsCheckingRestriction(true)

      try {
        // PRIORIDAD 1: mustChangePassword = true (funcionalidad existente)
        // Si mustChangePassword es true, dejar que CreatePasswordRoute maneje
        const mustChangePassword = authService.getMustChangePassword()
        if (mustChangePassword) {
          console.log(
            '🔑 mustChangePassword detected, letting CreatePasswordRoute handle navigation'
          )
          setIsCheckingRestriction(false)
          return
        }

        // PRIORIDAD 2: Usuario sin credit-application debe estar en pending-proposals
        const shouldBeRestricted =
          await authService.shouldBeRestrictedToPendingProposals()

        if (shouldBeRestricted) {
          console.log('🚫 User must be restricted to pending-proposals')

          if (!authService.isUserOnPendingProposalsWhenRequired()) {
            console.log('📍 Forcing redirect to /onboarding/pending-proposals')
            console.log('📍 From:', location.pathname + location.search)
            console.log('📍 To: /onboarding/pending-proposals')
            navigate('/onboarding/pending-proposals', { replace: true })
            return
          } else {
            console.log('✅ User is correctly on pending-proposals')
            setIsCheckingRestriction(false)
            return
          }
        }

        // PRIORIDAD 3: Verificar progreso activo normal
        if (!authService.hasActiveProgress()) {
          console.log('📝 No active progress, allowing free navigation')
          setIsCheckingRestriction(false)
          return
        }

        // Comportamiento híbrido (cualquier solicitud, no solo la 1ra): las
        // rutas del menú de cuenta (Mis cotizaciones y hermanas) son de
        // navegación libre aunque haya un progressUrl activo — ahí la
        // propia pantalla ofrece un botón "Continuar" para retomarlo, en
        // vez de forzar el redirect.
        if (
          !authService.isUserOnCorrectProgressURL() &&
          !isAccountMenuRoute(location.pathname)
        ) {
          const activeProgressURL = authService.getActiveProgressURL()
          if (activeProgressURL) {
            console.log('🚫 User not on correct progress URL, redirecting...')
            console.log(
              '📍 Redirecting from:',
              location.pathname + location.search
            )
            console.log('📍 Redirecting to:', activeProgressURL)
            navigate(activeProgressURL, { replace: true })
            return
          }
        }

        setIsCheckingRestriction(false)
      } catch (error) {
        console.error('❌ Error in progress compliance check:', error)
        setIsCheckingRestriction(false)
      }
    }

    // Verificar inmediatamente
    checkProgressCompliance()

    // También verificar cuando cambie la ubicación con un pequeño delay
    const timeoutId = setTimeout(checkProgressCompliance, 100)

    return () => clearTimeout(timeoutId)
  }, [location.pathname, location.search, navigate])

  // Verificar si debe mostrar loading mientras verifica o redirige
  if (isCheckingRestriction || authService.isAuthenticated()) {
    // PRIORIDAD 1: Si mustChangePassword = true, no mostrar loading aquí
    // Dejar que CreatePasswordRoute maneje todo
    const mustChangePassword = authService.getMustChangePassword()
    if (mustChangePassword) {
      // No interceptar, dejar que CreatePasswordRoute funcione
      return <>{children}</>
    }

    // PRIORIDAD 2: Verificar restricción a pending-proposals (solo si NO hay mustChangePassword)
    const currentPath = location.pathname
    if (
      currentPath !== '/onboarding/pending-proposals' &&
      isCheckingRestriction
    ) {
      console.log('⏳ Checking restriction to pending-proposals...')
      return <LoadingScreen message="Verificando acceso..." />
    }

    // PRIORIDAD 3: Verificar progreso normal (exceptuando las rutas de
    // navegación libre del menú de cuenta — ver ACCOUNT_MENU_ROUTES)
    if (
      authService.hasActiveProgress() &&
      !authService.isUserOnCorrectProgressURL() &&
      !isAccountMenuRoute(currentPath)
    ) {
      console.log(
        '⏳ Showing loading while redirecting to correct progress URL...'
      )
      return <LoadingScreen message="Redirigiendo al punto de progreso..." />
    }
  }

  return <>{children}</>
}
